import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import type { RegistrationById } from "../api/registrations.types";
import { updateSharedProcessingMutationOptions } from "../api/sharedProcessings";
import * as FormDialog from "../shared/FormDialog";

type SharedProcessingEditDialogProps = {
  onClose: () => void;
  sharedProcessing: RegistrationById;
};

type FormValues = { name: string };

export const SharedProcessingEditDialog: FC<SharedProcessingEditDialogProps> = ({ onClose, sharedProcessing }) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus } = useForm<FormValues>({
    defaultValues: { name: sharedProcessing.name },
  });
  const updateMutation = useMutation(updateSharedProcessingMutationOptions());

  FormDialog.useAutoFocusField(setFocus, "name");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("RegistrationDrawer.Edit.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(({ name }) =>
          updateMutation.mutate({ id: sharedProcessing.id, name }, { onSuccess: onClose })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={updateMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("RegistrationDrawer.Edit.Error") }}
              label={t("RegistrationDrawer.Edit.NameLabel")}
              placeholder={t("CreateRegistration.Dialog.Shared.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateMutation.isPending}>
            {t("RegistrationDrawer.Edit.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
